import { Loading } from '@/components/loading';
import { Alert } from '@/components/nativewind-ui/alert';
import {
  ProgressScreen,
  type ProgressStep
} from '@/components/progress-screen';
import { Button } from '@/components/ui/button';
import { Text } from '@/components/ui/text';
import { H2 } from '@/components/ui/typography';
import { VIDEO_CONTENT_TYPES } from '@/constants/VideoContentTypes';
import { useAuth } from '@/contexts/AuthContext';
import { PersonaImportContext } from '@/contexts/PersonaImportContext';
import { useSSE } from '@/hooks/use-sse';
import { useUpload } from '@/hooks/use-upload';
import type { AlertRef } from '@/types/alert';
import { generateAPIUrl } from '@/utils/api-url';
import {
  CLASSIFY_STEPS,
  type CombinedSteps
} from '@/utils/persona-import-steps';
import Head from 'expo-router/head';
import { fetch as expoFetch } from 'expo/fetch';
import { hc } from 'hono/client';
import { Fragment, useCallback, useContext, useRef, useState } from 'react';
import { Image, Platform, ScrollView, StyleSheet, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import type { ApiPersonaImportType } from '../api/persona-import/[...everything]+api';

const personaImportClient = hc<ApiPersonaImportType>(generateAPIUrl('/'), {
  fetch: expoFetch as unknown as typeof fetch
});

const IMAGE_SOURCE = require('../../assets/images/persona3.png');

async function uploadStart(
  token: string,
  { fileSize, contentType }: { fileSize: number; contentType: string }
) {
  const res = await personaImportClient.api['persona-import'].upload.$post(
    {
      json: {
        fileSize,
        contentType,
        platform: Platform.OS === 'web' ? 'web' : 'app'
      }
    },
    { headers: { Authorization: `Bearer ${token}` } }
  );

  if (!res.ok) {
    if (res.status === 429) {
      throw new Error(await res.text());
    }

    throw new Error(`Failed to upload, please try again: ${res.statusText}`);
  }

  const data = await res.json();
  if (data.valid !== true) {
    throw new Error(
      typeof data.error === 'string' ? data.error : 'Failed to upload'
    );
  }

  return data;
}

async function uploadComplete(token: string, uploadId: string) {
  const res = await personaImportClient.api['persona-import'][
    'upload-complete'
  ].$post(
    {
      json: {
        uploadId
      }
    },
    { headers: { Authorization: `Bearer ${token}` } }
  );

  if (!res.ok) {
    if (res.status === 429) {
      throw new Error(await res.text());
    }

    throw new Error(
      `Failed to mark upload as complete, please try again: ${res.statusText} ${await res.text()}`
    );
  }

  const data = await res.json();

  if (data.valid !== true) {
    throw new Error('Failed to mark upload as complete');
  }

  return data;
}

const initialSteps = CLASSIFY_STEPS.map((step) => ({
  stepType: step.stepType,
  completed: false
}));

export default function PersonaUploadLayout() {
  const { authState, user } = useAuth();
  const userId = user?.id;

  if (!authState.token || !userId) {
    return <Loading />;
  }

  return <PersonaUpload token={authState.token} />;
}

function PersonaUpload({ token }: { token: string }) {
  const { personaImport, updatePersonaImport, onNext } =
    useContext(PersonaImportContext);

  const [personaImportId, setPersonaImportId] = useState<string | undefined>(
    undefined
  );
  const setUploadStatus = useCallback(
    (uploadStatus: typeof personaImport.uploadStatus) =>
      updatePersonaImport({ uploadStatus }),
    [updatePersonaImport]
  );
  const [progress, setProgress] = useState(0);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const { getAssetInfo, startUpload, pickFile } = useUpload();
  const alertRef = useRef<AlertRef>(null);

  const pickVideo = useCallback(async () => {
    setUploadStatus('selecting');
    const fileUri = await pickFile('video');

    if (!fileUri) {
      setUploadStatus('idle');
      return;
    }

    setProgress(0);
    setUploadError(null);
    // we create a new persona import every time the user selects a new video
    // (old ones will be automatically deleted)
    setPersonaImportId(undefined);
    setUploadStatus('uploading');

    const fileInfo = await getAssetInfo(fileUri);
    if (fileInfo.size > 1 * 1024 * 1024 * 1024 /* 1GB */) {
      alert('File size too large, please upload a file under 1GB');
      setUploadStatus('idle');
      return;
    }

    if (!VIDEO_CONTENT_TYPES.includes(fileInfo.type)) {
      alert('Invalid file type. Please upload a video file.');
      setUploadStatus('idle');
      return;
    }

    try {
      const uploadStartResult = await uploadStart(token, {
        fileSize: fileInfo.size,
        contentType: fileInfo.type
      });

      // console.log('Upload completed!!', uploadStartResult.uploadId);
      startUpload({
        mimeType: fileInfo.type,
        presignedUrl: uploadStartResult.presignedUrl,
        fileUri,
        onProgress: (bytesUploaded, bytesTotal) => {
          const percentComplete = (bytesUploaded / bytesTotal) * 100;
          setProgress(percentComplete);
        },
        onSuccess: () => {
          setUploadStatus('finalizing');
          uploadComplete(token, uploadStartResult.uploadId)
            .then((result) => {
              setPersonaImportId(result.personaImportId);
            })
            .catch((error) => {
              console.log(error);
              setUploadStatus('error');
              setUploadError(error.toString());
            });
        },
        onError: (error) => {
          console.log(error);
          setUploadStatus('error');
          setUploadError(error.toString());
        }
      });
    } catch (error: any) {
      console.log(error);
      setUploadStatus('error');
      setUploadError(error.toString());
      return;
    }
  }, [setUploadStatus, getAssetInfo, startUpload, pickFile, token]);

  const progressText =
    Platform.OS === 'web'
      ? `Please don't leave the page while uploading`
      : `Please don't leave this screen while uploading`;

  return (
    <Fragment>
      <Head>
        <title>New Persona - Lexikon AI</title>
      </Head>
      <Alert
        ref={alertRef}
        title="Discard draft?"
        message="Are you sure you want to discard your Persona? You will lose all progress."
        buttons={[
          {
            text: 'Cancel',
            style: 'cancel'
          },
          {
            text: 'OK',
            onPress: () => {
              updatePersonaImport({
                uploadStatus: 'idle'
              });
            }
          }
        ]}
      />

      <SafeAreaView edges={['right', 'bottom', 'left']}>
        <ScrollView contentContainerClassName="flex">
          <View className="px-12 pt-4">
            <View className="web:overflow-y-auto web:pb-4 web:lg:w-[50%] web:lg:self-center">
              {['completed', 'finalizing', 'uploading'].includes(
                personaImport.uploadStatus
              ) ? null : (
                <View className="items-center pb-1">
                  <Image
                    source={IMAGE_SOURCE}
                    style={styles.image}
                    resizeMode="contain"
                  />
                  <H2 className="border-0 pb-1 pt-4 text-center">
                    Upload your screen recording
                  </H2>
                </View>
              )}

              {['selecting', 'idle', 'incomplete', 'error'].includes(
                personaImport.uploadStatus
              ) ? (
                <View className="mt-4 w-full flex-1 items-center justify-center">
                  <Button
                    className="w-full rounded-full"
                    variant={
                      personaImport.uploadStatus === 'completed'
                        ? 'outline'
                        : 'default'
                    }
                    disabled={['uploading', 'finalizing'].includes(
                      personaImport.uploadStatus
                    )}
                    onPress={pickVideo}
                  >
                    <Text>
                      {['uploading', 'finalizing'].includes(
                        personaImport.uploadStatus
                      )
                        ? 'Uploading…'
                        : 'Select Recording'}
                    </Text>
                  </Button>
                </View>
              ) : null}

              {personaImport.uploadStatus === 'selecting' ? (
                <View className="items-center gap-4 pt-4">
                  <Loading />
                </View>
              ) : personaImport.uploadStatus === 'idle' ||
                personaImport.uploadStatus ===
                  'incomplete' ? null : personaImport.uploadStatus ===
                'error' ? (
                <View className="gap-4 pt-4">
                  <Text>The upload failed. Please try again.</Text>
                  <Text>{uploadError}</Text>
                </View>
              ) : personaImportId ? (
                <ClassifyVideo
                  uploadProgress={progress}
                  personaImportId={personaImportId}
                  setUploadError={setUploadError}
                  progressText={progressText}
                />
              ) : (
                <ProgressScreen
                  progress={progress * 0.9}
                  progressText={progressText}
                  completed={personaImport.uploadStatus === 'completed'}
                  completedText="Upload complete!"
                  progressSteps={[
                    {
                      stepType: 'uploading',
                      completed: ['finalizing', 'completed'].includes(
                        personaImport.uploadStatus
                      )
                    },
                    ...initialSteps
                  ]}
                />
              )}

              {personaImport.uploadStatus === 'completed' ? (
                <View>
                  <Button
                    variant="default"
                    className="mt-4 w-full rounded-full"
                    onPress={() => {
                      updatePersonaImport({
                        personaImportId
                      });
                      onNext();
                    }}
                  >
                    <Text>Continue</Text>
                  </Button>
                  <Button
                    className="mt-8"
                    size="sm"
                    variant="link"
                    onPress={() => {
                      alertRef.current?.show();
                    }}
                  >
                    <Text>Start Over</Text>
                  </Button>
                </View>
              ) : null}
            </View>
          </View>
        </ScrollView>
      </SafeAreaView>
    </Fragment>
  );
}

function ClassifyVideo({
  personaImportId,
  uploadProgress,
  setUploadError,
  progressText
}: {
  personaImportId: string;
  uploadProgress: number;
  setUploadError: (error: string) => void;
  progressText: string;
}) {
  const { personaImport, updatePersonaImport } =
    useContext(PersonaImportContext);
  const [classifyProgress, setClassifyProgress] = useState(0);
  const [classifyStepDetails, setClassifyStepDetails] =
    useState<ProgressStep[]>(initialSteps);
  const [videoTruncation, setVideoTruncation] = useState<
    'free' | 'paid' | 'none' | undefined
  >(undefined);

  useSSE<{
    personaImport: {
      status: 'pending' | 'in-progress' | 'completed' | 'failed';
      videoTruncation: 'free' | 'paid' | 'none';
    };
    combinedSteps: CombinedSteps;
  }>(
    generateAPIUrl(`/api/persona-import/${personaImportId}/classify-status`),
    (event) => {
      setClassifyProgress(event.combinedSteps.progress);
      setClassifyStepDetails(event.combinedSteps.stepDetails);

      if (event.personaImport.videoTruncation) {
        setVideoTruncation(event.personaImport.videoTruncation);
      }

      if (event.combinedSteps.errors.length > 0) {
        updatePersonaImport({ uploadStatus: 'error' });
        setUploadError(event.combinedSteps.errors.join('\n'));
        return false;
      }

      if (event.combinedSteps.progress === 100) {
        updatePersonaImport({ uploadStatus: 'completed', personaImportId });
        return false;
      }

      return true;
    },
    (error) => {
      updatePersonaImport({ uploadStatus: 'error' });
      setUploadError(error.message);
    }
  );

  const combinedProgress = uploadProgress * 0.9 + classifyProgress * 0.1;

  return (
    <Fragment>
      <ProgressScreen
        progress={combinedProgress}
        progressText={progressText}
        completed={personaImport.uploadStatus === 'completed'}
        completedText="Upload complete!"
        progressSteps={[
          {
            stepType: 'uploading',
            completed: ['finalizing', 'completed'].includes(
              personaImport.uploadStatus
            )
          },
          ...classifyStepDetails
        ]}
      />

      {videoTruncation === 'paid' ? (
        <Text className="py-2 text-center text-gray-300">
          <Text className="font-medium">Note:</Text> Your video will be limited
          to the first 5 minutes.
        </Text>
      ) : videoTruncation === 'free' ? (
        <Text className="py-2 text-center text-gray-300">
          <Text className="font-medium">Note:</Text> Your video will be limited
          to 1 minute. After your upload, you can upgrade to Premium for
          extended video processing.
        </Text>
      ) : null}
    </Fragment>
  );
}

const styles = StyleSheet.create({
  image: {
    width: 250,
    height: 250
  }
});
